import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import style from '../common/SettingsList.module.scss';
import Layout from '../../common/Layout/Layout';
import Table from '../../common/table/Table';
import Empty from '../../common/empty/Empty';
import ButtonCommon from '../../common/button/ButtonCommon';
import Switch from '../../common/switch/Switch';
import NewSelect from '../../common/select/NewSelect';
import Pagination from '../../common/pagination/Pagination';
import dayjs from '../../../util/dayjs';
import { RoleType } from '../../../api/types/RoleType';
import { UseType } from '../../../api/types/UseType';
import {
  AdministratorFilter,
  getAdministrators,
  putAdministratorRoleType,
  putAdministratorUnlock,
  putAdministratorUseType,
} from '../../../api/function/administrator';
import { AdministratorDto } from '../../../api/function/administrator/rs';
import { resetPassword } from '../../../repositories/member/MemberRepository';
import { useAccountStore } from '../../account/hooks/useAccountStore';
import useServerPaging from '../common/useServerPaging';

const cx = classNames.bind(style);

export const ROLE_TYPE_NAME: Record<RoleType, string> = {
  MASTER: '마스터',
  BUSINESS_PLANNING: '사업기획팀',
  SALES: '영업팀',
  OPERATIONS: '운영팀',
};
const roleOptions = (Object.keys(ROLE_TYPE_NAME) as RoleType[]).map((v) => ({ label: ROLE_TYPE_NAME[v], value: v }));

const formatDateTime = (value: string | null) => (value ? dayjs(value).format('YYYY.MM.DD HH:mm') : '-');

const showServerError = (e: any) => {
  alert(e?.response?.data?.data?.cause ?? '처리하지 못했습니다. 잠시 후 다시 시도해 주세요.');
};

const AdministratorManagement = () => {
  const myEmail = useAccountStore((state) => state.data.email);
  const [administrators, setAdministrators] = useState<AdministratorDto[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [draft, setDraft] = useState<AdministratorFilter>({});
  const [filter, setFilter] = useState<AdministratorFilter>({});
  const { page, setPage, applyResult, resetToFirst } = useServerPaging();

  const fetchAdministrators = () =>
    getAdministrators(filter, page.page, page.size).then((rs) => {
      setAdministrators(rs.data.list);
      applyResult(rs.data.pagination);
    });

  useEffect(() => {
    fetchAdministrators();
  }, [filter, page.page, page.size]);

  const search = (e: React.FormEvent) => {
    e.preventDefault();
    resetToFirst();
    setFilter({ ...draft });
  };

  const run = async (action: () => Promise<unknown>) => {
    setIsProcessing(true);
    try {
      await action();
    } catch (e) {
      showServerError(e);
    } finally {
      // 실패해도 초기화처럼 일부 반영됐을 수 있어 목록을 다시 받는다
      await fetchAdministrators();
      setIsProcessing(false);
    }
  };

  const handleChangeRole = (administrator: AdministratorDto, roleType: RoleType) => {
    if (!confirm(`${administrator.name} 님의 권한을 ${ROLE_TYPE_NAME[roleType]}(으)로 바꿀까요?`)) return;
    run(() => putAdministratorRoleType(administrator.id, roleType));
  };

  const handleToggleUseType = (administrator: AdministratorDto) => {
    const next = administrator.useType === UseType.USE ? UseType.UNUSED : UseType.USE;
    const message =
      next === UseType.UNUSED
        ? `${administrator.name} 님의 계정을 사용 중지할까요? 접속 중이면 바로 로그아웃됩니다.`
        : `${administrator.name} 님의 계정을 다시 사용하게 할까요?`;
    if (!confirm(message)) return;
    run(() => putAdministratorUseType(administrator.id, next));
  };

  const handleUnlock = (administrator: AdministratorDto) => {
    if (!confirm(`${administrator.name} 님의 계정 잠금을 해제할까요?`)) return;
    run(() => putAdministratorUnlock(administrator.id));
  };

  const handleResetPassword = (administrator: AdministratorDto) => {
    if (
      !confirm(
        `${administrator.name} 님의 비밀번호를 초기화할까요? 임시 비밀번호가 계정 이메일로 발송되고 잠금도 함께 풀립니다.`
      )
    )
      return;
    run(() => resetPassword({ email: administrator.email }));
  };

  return (
    <Layout>
      <h2>관리자 계정</h2>
      <p className={cx('description')}>
        로그인 5회 실패 또는 90일 미접속 계정은 잠깁니다. 퇴직자는 사용 중지로 처리합니다(계정은 지우지 않으며 같은
        이메일로 다시 만들 수 없습니다). 변경 내역은 설정 &gt; 계정 변경 이력에서 봅니다.
      </p>
      <form className={cx('toolbar')} onSubmit={search}>
        <div className={cx('field')}>
          <label htmlFor={'administrator-keyword'}>이름 또는 이메일 (전체 일치)</label>
          <input
            id={'administrator-keyword'}
            value={draft.keyword ?? ''}
            placeholder={'예: 홍길동, name@midasin.com'}
            onChange={(e) => setDraft({ ...draft, keyword: e.target.value })}
          />
        </div>
        <div className={cx('field')}>
          <label htmlFor={'administrator-role'}>권한</label>
          <select
            id={'administrator-role'}
            value={draft.roleType ?? ''}
            onChange={(e) => setDraft({ ...draft, roleType: e.target.value })}
          >
            <option value={''}>전체</option>
            {roleOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className={cx('field')}>
          <label htmlFor={'administrator-use'}>사용</label>
          <select
            id={'administrator-use'}
            value={draft.useType ?? ''}
            onChange={(e) => setDraft({ ...draft, useType: e.target.value })}
          >
            <option value={''}>전체</option>
            <option value={UseType.USE}>사용</option>
            <option value={UseType.UNUSED}>사용 중지</option>
          </select>
        </div>
        <div className={cx('field')}>
          <label htmlFor={'administrator-locked'}>잠금</label>
          <select
            id={'administrator-locked'}
            value={draft.locked ?? ''}
            onChange={(e) => setDraft({ ...draft, locked: e.target.value })}
          >
            <option value={''}>전체</option>
            <option value={'true'}>잠김</option>
            <option value={'false'}>잠기지 않음</option>
          </select>
        </div>
        <ButtonCommon size={'small'} submitType>
          조회
        </ButtonCommon>
      </form>
      <Table className={cx('table')}>
        <colgroup>
          <col style={{ width: '11%' }} />
          <col style={{ width: '19%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '7%' }} />
          <col style={{ width: '13%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '14%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>이름</th>
            <th>이메일</th>
            <th>권한</th>
            <th>사용</th>
            <th>상태</th>
            <th>마지막 로그인</th>
            <th>비밀번호 만료일</th>
            <th>관리</th>
          </tr>
        </thead>
        <tbody>
          {administrators.map((administrator) => {
            const isMe = administrator.email === myEmail;
            // 내 정보를 받기 전에는 본인 행을 알 수 없어 모든 행을 잠근다
            const isLockedRow = !myEmail || isMe;
            const isUnused = administrator.useType !== UseType.USE;
            return (
              <tr key={administrator.id}>
                <td>
                  {administrator.name}
                  {isMe && <span className={cx('me')}>(나)</span>}
                </td>
                <td>{administrator.email}</td>
                <td>
                  {isLockedRow ? (
                    ROLE_TYPE_NAME[administrator.roleType]
                  ) : (
                    <div className={cx('role-select')}>
                      <NewSelect
                        size={'small'}
                        options={roleOptions}
                        disabled={isProcessing}
                        value={roleOptions.find((v) => v.value === administrator.roleType)}
                        onChange={(option) => option && handleChangeRole(administrator, option.value as RoleType)}
                      />
                    </div>
                  )}
                </td>
                <td>
                  <div className={cx('switch')}>
                    <Switch
                      isOn={!isUnused}
                      toggle={() => handleToggleUseType(administrator)}
                      disabled={isLockedRow || isProcessing}
                    />
                  </div>
                </td>
                <td>
                  <span className={cx('badges')}>
                    {isUnused && <span className={cx('badge')}>사용 중지</span>}
                    {administrator.locked && <span className={cx('badge', 'locked')}>잠김</span>}
                    {administrator.temporaryPasswordYn && <span className={cx('badge')}>임시 비밀번호</span>}
                    {!administrator.locked && administrator.loginFailCount > 0 && (
                      <span className={cx('badge')}>실패 {administrator.loginFailCount}회</span>
                    )}
                    {!isUnused &&
                      !administrator.locked &&
                      !administrator.temporaryPasswordYn &&
                      administrator.loginFailCount === 0 && <span className={cx('badge', 'ok')}>정상</span>}
                  </span>
                </td>
                <td>{formatDateTime(administrator.lastLoginDateTime)}</td>
                <td>{administrator.passwordExpiryDate ?? '-'}</td>
                <td>
                  <span className={cx('actions')}>
                    {administrator.locked && (
                      <ButtonCommon size={'small'} onClick={() => handleUnlock(administrator)} disabled={isProcessing}>
                        잠금 해제
                      </ButtonCommon>
                    )}
                    <ButtonCommon
                      size={'small'}
                      onClick={() => handleResetPassword(administrator)}
                      disabled={isProcessing}
                    >
                      비밀번호 초기화
                    </ButtonCommon>
                  </span>
                </td>
              </tr>
            );
          })}
          {!administrators.length && (
            <tr>
              <td colSpan={8}>
                <Empty />
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <Pagination page={page} countWord={'명'} pageHandler={setPage} />
    </Layout>
  );
};

export default AdministratorManagement;
